import React, { useState, useMemo, ReactElement } from "react";
import { gql, useMutation, useQuery } from "urql";
import { useLocation, Link } from "wouter";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { emptyObjectId } from "../../../common/src";
import {
    TextField,
    SelectField,
    DateField,
    NumberField,
    CheckBoxField,
    SubmitButton,
    SearchMultipleSelectField
} from "../../../components/src/form";
import { Alert, AlertVariant } from "../../../components/src/Alert";
import { TooltipLabel } from "../../../components/src/TooltipLabel";
import { useAlertTimeOut } from "../../../components/src/common/Utils";
import { formikUrqlErrorFormater } from "../../../components/src/common/formik-urql-error-helper";

import {
    CurrencyEnum,
    InstrumentCategory,
    InstrumentProductTypeEnum,
    InstrumentStatusEnum,
    InstrumentModelTypeEnum,
    InstrumentModelInput,
    InstrumentModelLegTypesEnum,
    DayAdjustmentEnum,
    CreateInstrumentInput,
    CicAreaEnum,
    CicTypeEnum,
    InstrumentFilterInput
} from "../types.generated";
import { SYSTEM_PARTY_ID } from "../Constants";

const CREATE_INSTRUMENT = gql`
    mutation createInstrument($input: CreateInstrumentInput!) {
        createInstrument(input: $input) {
            _id
        }
    }
`;

const GET_INSTRUMENTS = gql`
    query instruments($filter: InstrumentFilterInput) {
        instruments(filter: $filter) {
            _id
            name
        }
    }
`;

const GET_PARTIES = gql`
    query {
        clients: parties(filter: { typeIn: [Client] }) {
            _id
            name
        }
    }
`;

interface FormValues {
    name: string;
    currency: CurrencyEnum;
    expiryDate: string;
    notional: number | null;
    strike: number | null;
    underlyingId: string;
    volatility: number | null;
    payLeg: boolean;
    settlementType: string;
    premium: number | null;
    premiumPaymentDate: string;
    clientIds: string[];
}

const getDefaultFormData = (currency: CurrencyEnum): FormValues => {
    const today = new Date();
    const oneYearFromToday = new Date(today);
    oneYearFromToday.setFullYear(today.getFullYear() + 1);
    const expiryDate = oneYearFromToday.toISOString().slice(0, 10);

    return {
        name: "",
        currency: currency,
        expiryDate: expiryDate,
        notional: null,
        strike: null,
        underlyingId: currency === CurrencyEnum.SEK ? "558bc96133d8650bd478e359" : "",
        volatility: null,
        payLeg: true,
        settlementType: "Physical",
        premium: null,
        premiumPaymentDate: "",
        clientIds: []
    };
};

const defaultFormData: FormValues = getDefaultFormData(CurrencyEnum.SEK);

export function SwaptionInstrumentPage(): ReactElement {
    const [_, navigate] = useLocation();
    const [__, createInstrument] = useMutation(CREATE_INSTRUMENT);

    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyEnum>(defaultFormData.currency);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    const [instrumentsResult] = useQuery({
        query: GET_INSTRUMENTS,
        variables: {
            filter: {
                modelTypeIn: [InstrumentModelTypeEnum.GenericSwap],
                currencyIn: [selectedCurrency]
            } as InstrumentFilterInput
        }
    });

    const [{ data: partiesData }] = useQuery({
        query: GET_PARTIES,
        requestPolicy: "cache-first"
    });

    const underlyingOptions = useMemo(() => {
        const instruments = instrumentsResult.data?.instruments || [];
        return instruments
            .filter((inst) => inst.name.includes("/IRS/3M"))
            .sort((a, b) => {
                // Extract number from pattern like "/10Y" or "/5Y" at the end
                const extractNumber = (name: string): number => {
                    // Match pattern like "/10Y" or "/5Y" - look for / followed by digits followed by Y
                    const match = name.match(/\/(\d+)Y$/);
                    if (match) {
                        return parseInt(match[1], 10);
                    }
                    // Fallback: try to find any number before Y at the end
                    const fallbackMatch = name.match(/(\d+)Y$/);
                    return fallbackMatch ? parseInt(fallbackMatch[1], 10) : 0;
                };
                const numA = extractNumber(a.name);
                const numB = extractNumber(b.name);
                return numA - numB;
            });
    }, [instrumentsResult.data?.instruments]);

    return (
        <div className="mx-auto px-4 py-4">
            <div className="mb-2">
                <Link to={"/instruments"}>Instruments</Link>
            </div>
            <h4>New Swaption Instrument</h4>
            {alert.visible ? (
                <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                    {alert.message}
                </Alert>
            ) : null}
            <Formik
                initialValues={defaultFormData}
                validationSchema={Yup.object({
                    name: Yup.string().required("Name is required").min(2, "Name must be at least 2 characters"),
                    currency: Yup.string().required("Currency is required"),
                    expiryDate: Yup.string().required("Expiry Date is required"),
                    notional: Yup.number().nullable().required("Notional is required").min(0, "Notional must be positive"),
                    strike: Yup.number().nullable().required("Strike is required"),
                    underlyingId: Yup.string().required("Underlying is required"),
                    volatility: Yup.number().nullable().required("Volatility is required").min(0, "Volatility must be positive"),
                    payLeg: Yup.boolean().required(),
                    settlementType: Yup.string().required("Settlement Type is required"),
                    premium: Yup.number().nullable().required("Premium is required"),
                    premiumPaymentDate: Yup.string().required("Premium Payment Date is required"),
                    clientIds: Yup.array().of(Yup.string()).min(1, "At least one client is required")
                })}
                onSubmit={async (values, { setSubmitting, setErrors }) => {
                    // Build the Swaption model structure
                    const model: InstrumentModelInput = {
                        _t: InstrumentModelTypeEnum.Swaption,
                        accountId: null,
                        clientId: null,
                        expiryDate: null,
                        legs: [
                            {
                                _t: InstrumentModelLegTypesEnum.SwaptionLeg,
                                calendars: ["CaptorSweden"],
                                call: false,
                                currency: values.currency,
                                dayAdjustment: DayAdjustmentEnum.ModifiedFollowing,
                                exercise: "European",
                                expiry: values.expiryDate,
                                maturityDate: values.expiryDate,
                                notional: values.notional,
                                payLeg: values.payLeg,
                                settlementType: values.settlementType,
                                startDateOffset: 2,
                                strike: values.strike !== null && values.strike !== undefined ? values.strike / 100 : null,
                                underlyingId: values.underlyingId,
                                volatility: values.volatility !== null && values.volatility !== undefined ? values.volatility / 10000 : null
                            },
                            {
                                _t: InstrumentModelLegTypesEnum.FixedAmountLeg,
                                calendars: ["CaptorSweden"],
                                call: null,
                                creditSpread: null,
                                currency: values.currency,
                                dayAdjustment: DayAdjustmentEnum.Following,
                                dayCount: null,
                                exercise: null,
                                expiry: null,
                                fixingType: null,
                                flatAdjustment: null,
                                issuerId: emptyObjectId,
                                longStub: null,
                                maturityDate: values.expiryDate,
                                maturityPeriod: null,
                                multiplier: null,
                                notional: null,
                                payLeg: false,
                                rate: null,
                                rateIndexId: null,
                                recoveryRate: 1,
                                relativeAdjustment: null,
                                rollingDate: null,
                                rollingPeriod: null,
                                settlementType: null,
                                spread: null,
                                startDate: values.expiryDate,
                                startDateOffset: null,
                                startPeriod: null,
                                strike: null,
                                ultimateForwardRate: null,
                                underlyingId: null,
                                volatility: null,
                                zeroFloor: null,
                                cashFlows: [
                                    {
                                        endDate: null,
                                        exDate: values.expiryDate,
                                        fixings: null,
                                        notional: values.premium,
                                        payDate: values.premiumPaymentDate,
                                        startDate: values.expiryDate
                                    }
                                ],
                                underlyings: null
                            }
                        ],
                        objectId: null,
                        objectType: null,
                        notionalScaling: 1,
                        quoteCurrency: values.currency,
                        quoteType: "Pct of Nominal",
                        startDate: null
                    };

                    const input: CreateInstrumentInput = {
                        name: values.name,
                        clientIds: values.clientIds && values.clientIds.length > 0 ? values.clientIds : [SYSTEM_PARTY_ID],
                        currency: values.currency,
                        isin: null,
                        longName: null,
                        status: InstrumentStatusEnum.Confirmed,
                        category: InstrumentCategory.InterestRate,
                        productType: InstrumentProductTypeEnum.Option,
                        bloombergQuoteMultiplier: 1.0,
                        cicArea: CicAreaEnum.XL,
                        cicType: CicTypeEnum.XXC6,
                        model: model
                    };

                    await createInstrument({ input })
                        .then((result) => {
                            if ("error" in result && result.error) {
                                const message = formikUrqlErrorFormater(result.error, setErrors);
                                setAlert({ color: "danger", visible: true, message });
                            } else {
                                const newInstrId = result.data.createInstrument._id;
                                setAlert({
                                    color: "success",
                                    visible: true,
                                    message: `New instrument '${values.name}' created successfully!`
                                });
                                // redirect to edit instrument page on success
                                if (newInstrId) {
                                    navigate(`/instruments/${newInstrId}`, { replace: true });
                                }
                            }
                            return true;
                        })
                        .catch((error) => {
                            setAlert({ color: "danger", visible: true, message: error.toString() });
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting, values, setFieldValue }) => {
                    // Update selectedCurrency when form currency changes
                    if (values.currency !== selectedCurrency) {
                        setSelectedCurrency(values.currency);
                        // Set underlyingId only if SEK is selected and it's currently empty
                        if (values.currency === CurrencyEnum.SEK && !values.underlyingId) {
                            setFieldValue("underlyingId", "558bc96133d8650bd478e359");
                        } else if (values.currency !== CurrencyEnum.SEK && values.underlyingId === "558bc96133d8650bd478e359") {
                            setFieldValue("underlyingId", "");
                        }
                    }

                    const generateName = () => {
                        const parts: string[] = [];

                        // Currency
                        parts.push(values.currency || "XXX");

                        // SWPTN
                        parts.push("SWPTN");

                        // P (payer) or R (receiver) based on payLeg
                        parts.push(values.payLeg ? "P" : "R");

                        // Strike - already in percentage format (3.25)
                        if (values.strike !== null && values.strike !== undefined) {
                            parts.push(values.strike.toFixed(2));
                        } else {
                            parts.push("XXX");
                        }

                        // Expiry date in YYYYMMDD format
                        if (values.expiryDate) {
                            const expiryDateFormatted = values.expiryDate.replace(/-/g, "");
                            parts.push(expiryDateFormatted);
                        } else {
                            parts.push("XXX");
                        }

                        // Tenor from underlying instrument
                        if (values.underlyingId) {
                            const selectedUnderlying = underlyingOptions.find((inst) => inst._id === values.underlyingId);
                            if (selectedUnderlying) {
                                // Extract tenor like "10Y" from name
                                const tenorMatch = selectedUnderlying.name.match(/(\d+Y)$/);
                                if (tenorMatch) {
                                    parts.push(tenorMatch[1]);
                                } else {
                                    parts.push("XXX");
                                }
                            } else {
                                parts.push("XXX");
                            }
                        } else {
                            parts.push("XXX");
                        }

                        setFieldValue("name", parts.join("/"));
                    };

                    return (
                        <Form autoComplete="off">
                            <div className="flex flex-wrap -mx-2">
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <div className="mb-4 w-full">
                                        <label htmlFor="name" className="block mb-1">
                                            Name
                                        </label>
                                        <div className="flex gap-2">
                                            <div className="flex-1 [&>div]:mb-0">
                                                <TextField name="name" label={null} className="mb-0" disabled={isSubmitting} />
                                            </div>
                                            <div className="flex items-center">
                                                <button
                                                    type="button"
                                                    onClick={generateName}
                                                    disabled={isSubmitting}
                                                    className="px-3 py-[6px] text-sm bg-sky-600 text-white rounded hover:bg-sky-700 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                                                >
                                                    Generate name
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full md:w-[5.56%] min-w-[100px] px-2 mb-4">
                                    <SelectField
                                        name="currency"
                                        label="Currency"
                                        className=""
                                        disabled={isSubmitting}
                                        options={Object.keys(CurrencyEnum).sort()}
                                    />
                                </div>
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <DateField name="expiryDate" label="Expiry Date" className="" disabled={isSubmitting} />
                                </div>
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <NumberField name="notional" label="Notional" className="" disabled={isSubmitting} />
                                </div>
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <NumberField name="strike" label="Strike (%)" className="" disabled={isSubmitting} />
                                </div>
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <SelectField
                                        name="underlyingId"
                                        label={<TooltipLabel text="Underlying" content="Only swaps 3m vs fixed" className="d-inline" />}
                                        className=""
                                        disabled={isSubmitting}
                                        options={underlyingOptions.map((inst) => ({ key: inst._id, value: inst.name }))}
                                    />
                                </div>
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <NumberField name="volatility" label="Volatility (bp)" className="" disabled={isSubmitting} />
                                </div>
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <CheckBoxField
                                        name="payLeg"
                                        label="Payleg (checked is short payer)"
                                        className=""
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <SelectField
                                        name="settlementType"
                                        label="Settlement Type"
                                        className=""
                                        disabled={isSubmitting}
                                        options={["Physical", "Cash"]}
                                    />
                                </div>
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <NumberField name="premium" label="Premium" className="" disabled={isSubmitting} />
                                </div>
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <DateField
                                        name="premiumPaymentDate"
                                        label="Premium Payment Date"
                                        className=""
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className="w-full lg:w-4/12 md:w-4/12 sm:w-3/12 px-2 mb-4">
                                    <SearchMultipleSelectField
                                        name="clientIds"
                                        label="Clients (owner)"
                                        className="w-100"
                                        disabled={isSubmitting}
                                        options={partiesData?.clients?.map((d) => ({ key: d._id, value: d.name, text: d.name })) || []}
                                    />
                                </div>
                            </div>
                            <div className="mt-4">
                                <SubmitButton disabled={isSubmitting} label="Create Instrument" />
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
}
